<template>
  <div class="pf" :aria-label="`Путь процесса: ${title}`">
    <div class="pf-head">
      <span :class="['pf-dot', `pf-dot--${flow?.light || 'grey'}`]" :title="LIGHT_TITLES[flow?.light || 'grey']"></span>
      <b class="pf-title">{{ title }}</b>
      <span class="pf-headline">{{ flowHeadline(flow) }}</span>
      <span v-if="flow && flow.open" class="pf-owner" title="Чей сейчас ход по открытым">
        ход за нами — {{ flow.we_count || 0 }}, ждём других — {{ flow.them_count || 0 }}
      </span>
    </div>

    <ol class="pf-stages">
      <li v-for="(s, i) in stages" :key="s.key">
        <button
          type="button"
          :class="['pf-stage', `pf-stage--${s.count ? s.light : 'empty'}`, { 'is-active': active === s.key }]"
          :title="s.hint"
          @click="$emit('stage', active === s.key ? '' : s.key)"
        >
          <span class="pf-stage__n">{{ s.count }}</span>
          <span class="pf-stage__t">{{ s.title }}</span>
          <span v-if="s.stuck" class="pf-stage__stuck">⏸ {{ s.stuck }}</span>
          <span class="pf-stage__bar"><i :style="{ width: stageShare(s, flow) + '%' }"></i></span>
        </button>
        <span v-if="i < stages.length - 1" class="pf-arrow" aria-hidden="true">→</span>
      </li>
    </ol>

    <div v-if="urgent.length || stuck.length" class="pf-lists">
      <div v-if="urgent.length" class="pf-list pf-list--red">
        <p class="pf-list__h">🔥 Срочно <span class="pf-muted">— срок горит или прошёл</span></p>
        <button v-for="i in urgent" :key="'u' + i.id" type="button" class="pf-row" @click="$emit('open', i.id)">
          <b>№{{ i.id }}</b> {{ i.title }} <span class="pf-muted">· {{ i.status_title }}</span>
          <span class="pf-row__due">{{ dueOf(i) }}</span>
        </button>
      </div>
      <div v-if="stuck.length" class="pf-list pf-list--yellow">
        <p class="pf-list__h">⏸ Зависло <span class="pf-muted">— ход за нами, а движения нет</span></p>
        <button v-for="i in stuck" :key="'s' + i.id" type="button" class="pf-row" @click="$emit('open', i.id)">
          <b>№{{ i.id }}</b> {{ i.title }} <span class="pf-muted">· {{ i.status_title }}</span>
          <span class="pf-row__due">{{ stalledText(i.stalled_days) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
// Путь процесса: этапы с количеством и цветом, «Срочно», «Зависло». Общий компонент — для любого процесса. [Claude]
import { computed } from 'vue'
import { LIGHT_TITLES, flowHeadline, pickByIds, stageShare, stalledText } from '@/components/process/processFlow.mjs'

const props = defineProps({
  title: { type: String, required: true },
  flow: { type: Object, default: null },
  items: { type: Array, default: () => [] },
  active: { type: String, default: '' },
  dueText: { type: Function, default: null },
})
defineEmits(['stage', 'open'])

const stages = computed(() => props.flow?.stages || [])
const urgent = computed(() => pickByIds(props.items, props.flow?.urgent))
const stuck = computed(() => pickByIds(props.items, props.flow?.stuck))
const dueOf = (i) => (props.dueText ? props.dueText(i) : i.due || '')
</script>

<style scoped>
.pf { --red: #dc2626; --yellow: #d97706; --green: #16a34a; --grey: #9ca3af; --line: #e7e5df; --muted: #6b7280; --gold: #f2b705;
  display: flex; flex-direction: column; gap: 10px; padding: 12px 14px; border-radius: 16px; background: #fbfaf6; border: 1px solid var(--line); }
.pf-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13.5px; }
.pf-title { font-size: 14.5px; }
.pf-headline { color: #374151; }
.pf-owner { margin-left: auto; color: var(--muted); font-size: 12.5px; }
.pf-muted { color: var(--muted); font-weight: 400; }
.pf-dot { width: 11px; height: 11px; border-radius: 50%; background: var(--grey); }
.pf-dot--red { background: var(--red); animation: pf-pulse 1.6s ease-in-out infinite; } .pf-dot--yellow { background: var(--yellow); } .pf-dot--green { background: var(--green); }
.pf-stages { list-style: none; margin: 0; padding: 0; display: flex; align-items: stretch; gap: 4px; overflow-x: auto; scrollbar-width: thin; }
.pf-stages li { display: flex; align-items: center; gap: 4px; }
.pf-stage { position: relative; min-width: 112px; display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 8px 10px 10px;
  border-radius: 12px; border: 1px solid var(--line); background: #fff; font: inherit; cursor: pointer; text-align: left; transition: transform .2s, box-shadow .2s; }
.pf-stage:hover { transform: translateY(-1px); box-shadow: 0 10px 20px -16px rgba(31, 36, 48, .6); }
.pf-stage.is-active { outline: 2px solid var(--gold); }
.pf-stage__n { font-size: 18px; font-weight: 750; line-height: 1; }
.pf-stage__t { font-size: 12px; color: #374151; }
.pf-stage__stuck { position: absolute; top: 6px; right: 8px; font-size: 11px; color: var(--yellow); font-weight: 700; }
.pf-stage__bar { position: absolute; left: 10px; right: 10px; bottom: 4px; height: 3px; border-radius: 3px; background: #f0eee8; overflow: hidden; }
.pf-stage__bar i { display: block; height: 100%; background: var(--grey); transition: width .4s ease; }
.pf-stage--red { border-color: rgba(220, 38, 38, .45); } .pf-stage--red .pf-stage__n { color: var(--red); } .pf-stage--red .pf-stage__bar i { background: var(--red); }
.pf-stage--yellow { border-color: rgba(217, 119, 6, .45); } .pf-stage--yellow .pf-stage__n { color: var(--yellow); } .pf-stage--yellow .pf-stage__bar i { background: var(--yellow); }
.pf-stage--green .pf-stage__bar i { background: var(--green); }
.pf-stage--empty { opacity: .55; }
.pf-arrow { color: var(--muted); }
.pf-lists { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 8px; }
.pf-list { border-radius: 12px; padding: 8px 10px; background: #fff; border: 1px solid var(--line); display: flex; flex-direction: column; gap: 2px; }
.pf-list--red { border-left: 4px solid var(--red); } .pf-list--yellow { border-left: 4px solid var(--yellow); }
.pf-list__h { margin: 0 0 4px; font-size: 13px; font-weight: 700; }
.pf-row { display: flex; gap: 6px; align-items: baseline; border: 0; background: none; padding: 5px 4px; border-radius: 8px; font: inherit; font-size: 12.5px; cursor: pointer; text-align: left; }
.pf-row:hover { background: #f7f5ef; }
.pf-row__due { margin-left: auto; white-space: nowrap; color: var(--muted); }
@keyframes pf-pulse { 50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, .12); } }
@media (prefers-reduced-motion: reduce) { .pf-dot--red { animation: none; } }
</style>
